import React from "react";
import { Alert, Badge, Card, Divider, Group, Paper, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconBuilding, IconCalendarDue, IconCheck } from "@tabler/icons-react";
import { HotelDto } from "../../api/hotelBookingApi";
import { formatDateTime, formatMoney } from "../../utils/hotelBookingUtils";
import { HotelBookingTexts } from "./HotelBookingTypes";

type Props = {
    hotels: HotelDto[];
    selectedHotelId: number | null;
    onSelectHotel: (hotelId: number | null) => void;
    t: HotelBookingTexts;
};

export default function HotelSelector({ hotels, selectedHotelId, onSelectHotel, t }: Props) {
    const activeHotels = hotels.filter((h) => h.isActive);

    const hotelSelectData = activeHotels.map((h) => ({
        value: String(h.hotelID),
        label: h.name,
    }));

    return (
        <Paper withBorder p="lg" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="flex-end">
                    <Stack gap={2}>
                        <Title order={4}>{t.chooseHotel}</Title>
                        <Text c="dimmed" size="sm">{t.chooseHotelText}</Text>
                    </Stack>


                </Group>

                {activeHotels.length === 0 ? (
                    <Alert color="blue" variant="light">
                        {t.noHotels}
                    </Alert>
                ) : (
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                        {activeHotels.map((hotel) => {
                            const selected = hotel.hotelID === selectedHotelId;
                            const activeRoomTypes = hotel.roomTypes?.filter((r) => r.isActive) ?? [];
                            const minPrice = activeRoomTypes.length
                                ? Math.min(...activeRoomTypes.map((r) => r.pricePerNight))
                                : 0;

                            return (
                                <Card
                                    key={hotel.hotelID}
                                    withBorder
                                    radius="lg"
                                    p="lg"
                                    onClick={() => onSelectHotel(hotel.hotelID)}
                                    style={{
                                        cursor: "pointer",
                                        borderColor: selected ? "red" : undefined,
                                        boxShadow: selected
                                            ? "0 10px 30px rgba(229, 58, 58, 0.18)"
                                            : undefined,
                                    }}
                                >
                                    <Stack gap="sm">
                                        <Group justify="space-between" align="flex-start">
                                            <Group gap="sm">
                                                <IconBuilding size={24} />
                                                <Stack gap={0}>
                                                    <Text fw={800}>{hotel.name}</Text>
                                                    <Text size="xs" c="dimmed">
                                                        {hotel.address || t.noAddress}
                                                    </Text>
                                                </Stack>
                                            </Group>

                                            {selected && (
                                                <Badge color="red" leftSection={<IconCheck size={12} />}>
                                                    {t.selected}
                                                </Badge>
                                            )}
                                        </Group>

                                        {hotel.description && (
                                            <Text size="sm" c="dimmed" lineClamp={2}>
                                                {hotel.description}
                                            </Text>
                                        )}

                                        <Divider />

                                        <Group justify="space-between">
                                            <Stack gap={0}>
                                                <Text size="xs" c="dimmed">{t.fromPrice}</Text>
                                                <Text fw={800}>{minPrice > 0 ? formatMoney(minPrice) : "-"}</Text>
                                            </Stack>

                                            <Stack gap={0} align="flex-end">
                                                <Text size="xs" c="dimmed">{t.roomTypes}</Text>
                                                <Text fw={800}>{activeRoomTypes.length}</Text>
                                            </Stack>
                                        </Group>

                                        <Group gap={6}>
                                            <IconCalendarDue size={15} />
                                            <Text size="xs" c="dimmed">
                                                {t.deadline}: {formatDateTime(hotel.bookingDeadline)}
                                            </Text>
                                        </Group>
                                    </Stack>
                                </Card>
                            );
                        })}
                    </SimpleGrid>
                )}
            </Stack>
        </Paper>
    );
}
